import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { X, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrutalButton } from "@/components/dashboard/BentoCard";
import { PROPERTIES } from "@/constants/testIds";

export function PropertyDrawer({ row, onClose }) {
  const warnings = Array.isArray(row.warnings) ? row.warnings : [];
  return (
    <div className="fixed inset-0 z-50 flex" data-testid={PROPERTIES.drawer}>
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white border-l-2 border-black h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b-2 border-black px-6 py-4 flex items-start justify-between">
          <div>
            <div className="data-label">Parcel</div>
            <div className="font-display font-bold text-2xl tracking-tight">
              {row.parcel_number || "—"}
            </div>
            <div className="text-sm text-zinc-600 mt-1">{row.address || "—"}</div>
            {row.deal_score ? (
              <div className="mt-3 inline-flex items-center gap-2 border-2 border-black bg-yellow-50 px-3 py-1">
                <span className="font-mono-data text-[10px] uppercase tracking-widest">AI Deal Score</span>
                <span className="font-display font-bold text-lg text-red-600">{row.deal_score.score}/100</span>
                <span className="font-mono-data text-[10px] uppercase text-zinc-500">{row.deal_score.label}</span>
              </div>
            ) : null}
          </div>
          <button
            data-testid={PROPERTIES.drawerClose}
            onClick={onClose}
            className="p-2 hover:bg-zinc-100 border-2 border-transparent hover:border-black"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <DetailRow label="Owner" value={row.owner} />
            <DetailRow label="Amount Owed" value={fmtMoney(row.amount_owed)} mono />
            <DetailRow label="Sale Date" value={row.sale_date} mono />
            <DetailRow label="Status" value={row.status} />
            <DetailRow label="Type" value={row.property_type} />
            <DetailRow label="Acres" value={row.acres} mono />
            <DetailRow label="City" value={row.city} />
            <DetailRow label="State" value={row.state} mono />
            <DetailRow label="Jurisdiction" value={row.jurisdiction} />
            <DetailRow label="Source ID" value={row.source_id} mono />
            <DetailRow label="Instrument #" value={row.instrument_number} mono />
          </div>

          <ValuationSection row={row} />

          {row.source_url ? (
            <div>
              <div className="data-label mb-1">Source URL</div>
              <a
                href={row.source_url}
                target="_blank"
                rel="noreferrer"
                className="font-mono-data text-yellow-700 text-sm hover:underline break-all"
              >
                {row.source_url}
              </a>
            </div>
          ) : null}

          {warnings.length > 0 && (
            <div className="border-2 border-yellow-500 bg-yellow-50 p-4">
              <div className="data-label text-yellow-800 mb-2">Warnings</div>
              <ul className="list-disc list-inside space-y-1 text-sm text-yellow-900">
                {warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <div className="data-label mb-2">Raw Record</div>
            <pre className="font-mono-data text-xs bg-zinc-950 text-yellow-100 p-4 border-2 border-black overflow-auto max-h-96">
              {JSON.stringify(row.raw_record ?? {}, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

function ValuationSection({ row }) {
  const { isSubscribed } = useAuth();
  const [result, setResult] = useState(
    row.valuation && !row.valuation._locked ? { valuation: row.valuation } : null
  );
  const mutation = useMutation({
    mutationFn: async () => (await api.post(`/properties/${row.id}/valuation`)).data,
    onSuccess: (data) => {
      setResult(data);
      toast.success(data.cached ? "Valuation loaded from cache" : "RentCast valuation retrieved");
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not fetch valuation");
    },
  });

  const val = result?.valuation;
  const ltv = val?.estimated_value && row.amount_owed ? row.amount_owed / val.estimated_value : null;

  return (
    <div className="border-2 border-black bg-zinc-50 p-4" data-testid="valuation-section">
      <div className="flex items-center justify-between mb-2">
        <div className="data-label flex items-center gap-1">
          <Sparkles size={12} /> Real Value Estimate
        </div>
        <span className="font-mono-data text-[10px] uppercase text-zinc-400">RentCast AVM</span>
      </div>
      {!isSubscribed ? (
        <div className="flex items-center gap-2 text-sm text-zinc-600" data-testid="valuation-locked">
          <Lock size={14} />
          <span>
            <Link to="/pricing" className="text-red-600 font-semibold hover:underline">
              Subscribe
            </Link>{" "}
            to unlock live market valuations.
          </span>
        </div>
      ) : val ? (
        <div className="space-y-2" data-testid="valuation-result">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-display font-bold text-2xl text-red-600" data-testid="valuation-value">
              {fmtMoney(val.estimated_value)}
            </span>
            <span className="font-mono-data text-xs text-zinc-500">
              {fmtMoney(val.value_range_low)} – {fmtMoney(val.value_range_high)}
            </span>
            {ltv != null && ltv < 0.15 ? (
              <span
                data-testid="valuation-hot-deal-badge"
                className="bg-red-600 text-white font-mono-data text-[10px] uppercase tracking-widest px-2 py-0.5 border border-black"
              >
                Hot deal · &lt;15% LTV
              </span>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="data-label">Lien-to-Value</div>
              <div className="font-mono-data mt-1" data-testid="valuation-ltv">
                {ltv != null ? `${(ltv * 100).toFixed(1)}%` : "—"}
              </div>
            </div>
            <div>
              <div className="data-label">Comps Used</div>
              <div className="font-mono-data mt-1">{val.comp_count ?? "—"}</div>
            </div>
          </div>
          {result?.deal_score ? (
            <div className="text-xs text-zinc-600" data-testid="valuation-updated-score">
              Updated AI Deal Score:{" "}
              <span className="font-display font-bold text-red-600">{result.deal_score.score}/100</span>{" "}
              <span className="uppercase font-mono-data">{result.deal_score.label}</span>
            </div>
          ) : null}
        </div>
      ) : !row.address ? (
        <p className="text-sm text-zinc-500" data-testid="valuation-no-address">
          No street address on record — valuation unavailable.
        </p>
      ) : (
        <BrutalButton
          data-testid="get-valuation-btn"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          <Sparkles size={14} /> {mutation.isPending ? "Fetching…" : "Get Market Value"}
        </BrutalButton>
      )}
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div>
      <div className="data-label">{label}</div>
      <div className={`mt-1 text-sm ${mono ? "font-mono-data" : ""}`}>
        {value === null || value === undefined || value === "" ? "—" : String(value)}
      </div>
    </div>
  );
}
